import { Table as ChakraTable, IconButton } from "@chakra-ui/react";
import { useState } from "react";

import GenericTable from "@/components/GenericTable";
import GenericTableElement from "@/components/GenericTableElement";
import { toaster } from "@/components/ui/toaster";
import { CUSTOMER_TABLE_COLUMNS } from "@/config/tables";
import { useMerchant } from "@/hooks/useMerchant";
import { CreateCustomer, Customer } from "@/types/types";
import { Pencil } from "lucide-react";
import { useEditCreateCustomer } from "../hooks/useEditCreateCustomer";
import AddCustomerModal from "./AddCustomerModal";
import { usePermissions } from "@/hooks/usePermissions";

type Props = {
  isLoading: boolean;
  customers: Customer[];
  refetch: () => void;
};

const Table = ({ isLoading, customers, refetch }: Props): JSX.Element => {
  const { merchant } = useMerchant();
  const { isEditor } = usePermissions();

  const [isEditingCustomer, setIsEditingCustomer] = useState(false);
  const [editCustomerData, setEditCustomerData] = useState<
    Customer | undefined
  >(undefined);

  const tableColumns = isEditor
    ? CUSTOMER_TABLE_COLUMNS
    : CUSTOMER_TABLE_COLUMNS.filter((column) => column.key !== "edit");

  const editCustomerManually = useEditCreateCustomer(
    () => {
      toaster.create({
        title: "Cliente actualizado",
        description: "El cliente se ha actualizado con éxito.",
        type: "success",
        duration: 5000,
        meta: { closable: true },
      });
      refetch();
    },
    () => {
      toaster.create({
        title: "Error",
        description: "Hubo un error al actualizar el cliente.",
        type: "error",
        duration: 5000,
        meta: { closable: true },
      });
    }
  );

  async function handleEditCustomer(
    newCustomerData: CreateCustomer
  ): Promise<void> {
    try {
      await editCustomerManually(newCustomerData);
    } finally {
      setIsEditingCustomer(false);
    }
  }

  return (
    <>
      <GenericTable
        isLoading={isLoading}
        columns={tableColumns.filter(
          (column) =>
            merchant?.customerRiskScoring || column.key !== "riskScore"
        )}
      >
        {customers.map((customer, index) => (
          <ChakraTable.Row key={index}>
            {tableColumns
              .filter(
                (column) =>
                  merchant?.customerRiskScoring || column.key !== "riskScore"
              )
              .map((column) => (
                <GenericTableElement
                  key={`${index}-${column.key}`}
                  value={
                    "value" in column
                      ? column.value(customer)
                      : customer[column.key as keyof Customer]
                  }
                  formatDatetimeUtc={column.key === "createdAt"}
                  statusIcon={column.key === "muted"}
                  badge={column.key === "riskScore"}
                  actions={
                    column.key === "edit" ? (
                      <IconButton
                        variant="ghost"
                        aria-label="Editar cliente"
                        onClick={() => {
                          setEditCustomerData(customer);
                          setIsEditingCustomer(true);
                        }}
                      >
                        <Pencil size="1.2rem" />
                      </IconButton>
                    ) : undefined
                  }
                />
              ))}
          </ChakraTable.Row>
        ))}
      </GenericTable>

      <AddCustomerModal
        open={isEditingCustomer}
        onClose={() => {
          setIsEditingCustomer(false);
        }}
        onSubmit={handleEditCustomer}
        editCustomerData={editCustomerData}
      />
    </>
  );
};

export default Table;
